import {
  Box,
  Button,
  Card,
  FormField,
  Heading,
  TextInput,
  Notification,
  Text,
  Select,
} from "grommet";
import { useEffect, useState } from "react";
import { API } from "aws-amplify";
import { ListOrganizations } from "../graphql/queries";
import { fetchGoogleRoomDetails } from "../Components/PodV2/api";

function sortOrganizations(organizations) {
  return [...organizations].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, {
      sensitivity: "base",
    }),
  );
}

function GoogleRooms() {
  const [organizations, setOrganizations] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const [meetLink, setMeetLink] = useState("");
  const [meetLoading, setMeetLoading] = useState(false);
  const [meetError, setMeetError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchOrganizations() {
      setLoadingOrganizations(true);

      try {
        const { data } = await API.graphql({ query: ListOrganizations });
        const parsed = JSON.parse(data.listOrganizations);
        const items = Array.isArray(parsed.body) ? parsed.body : [];
        const sorted = sortOrganizations(items);

        if (cancelled) return;

        setOrganizations(sorted);
        setOrganizationOptions(sorted);
      } catch (error) {
        console.error("Error fetching organizations:", error);
        if (!cancelled) {
          setOrganizations([]);
          setOrganizationOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingOrganizations(false);
        }
      }
    }

    fetchOrganizations();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleOrganizationSearch = (searchTerm) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      setOrganizationOptions(organizations);
      return;
    }

    setOrganizationOptions(
      organizations.filter((organizationOption) =>
        (organizationOption.name || "")
          .toLowerCase()
          .includes(normalizedSearch),
      ),
    );
  };

  const handleOrganizationSelect = ({ option }) => {
    setSelectedOrganization(option);
    setMeetLink("");
    setMeetLoading(false);
    setMeetError(false);
    setCopied(false);
  };

  const handleGenerateMeetLink = async () => {
    if (!selectedOrganization?.id) return;

    setMeetLoading(true);
    setMeetError(false);
    setMeetLink("");
    setCopied(false);

    try {
      const parsed = await fetchGoogleRoomDetails("createRoom", {
        clientId: selectedOrganization.id,
      });

      if (parsed.statusCode === 200 && parsed.body[0]?.meetingUri) {
        setMeetLink(parsed.body[0].meetingUri);
      } else {
        setMeetError(true);
      }
    } catch (error) {
      console.error(error);
      setMeetError(true);
    } finally {
      setMeetLoading(false);
    }
  };

  return (
    <Box pad="medium" justify="center" align="center">
      {meetError && (
        <Notification
          toast
          title="Error"
          status="critical"
          message="Something went wrong generating the Meet link. Please try again."
          onClose={() => setMeetError(false)}
        />
      )}
      {copied && (
        <Notification
          toast
          status="normal"
          message="Meet link copied to clipboard"
          onClose={() => setCopied(false)}
        />
      )}

      <Heading level="3" textAlign="center">
        Google Rooms
      </Heading>

      <Card pad="medium" width="medium" gap="medium">
        <FormField label="Organization">
          <Select
            placeholder="Select organization"
            options={organizationOptions}
            labelKey="name"
            valueKey="id"
            value={selectedOrganization || ""}
            onSearch={handleOrganizationSearch}
            onChange={handleOrganizationSelect}
            searchPlaceholder="Search organizations"
            disabled={loadingOrganizations}
          />
        </FormField>

        {selectedOrganization && (
          <Box align="center" gap="small">
            <Text textAlign="center">
              Generate a Google Meet link for {selectedOrganization.name}.
            </Text>
            <Button
              label={
                meetLoading ? "Generating..." : "Generate Google Meet Link"
              }
              primary
              color="#BFBFBF"
              disabled={meetLoading}
              onClick={handleGenerateMeetLink}
            />
            {meetLink && (
              <Box margin={{ top: "small" }} gap="small" width="100%">
                <Text size="small" weight="bold">
                  Google Meet Link
                </Text>
                <TextInput value={meetLink} readOnly size="small" />
                <Button
                  label="Copy Link"
                  primary
                  color="#BFBFBF"
                  onClick={() => {
                    navigator.clipboard.writeText(meetLink);
                    setCopied(true);
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
}

export default GoogleRooms;
